import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import classnames from 'classnames';
import { Button, Modal } from 'components/common';
import { billing, noop, useResponsiveWidth } from 'libs';
import { initialState } from 'state';
import CurrentCard from './CurrentCard';
import PaymentForm from './PaymentForm';
import TrialOfferSummary from './TrialOfferSummary';
import type { PaymentFormSubmitData, TrialConfig, TrialOffer } from './types';

interface User {
  features: {
    eligible_for_free_trial?: boolean;
    has_payment_method?: boolean;
  };
  pm_last_four?: string;
  pm_type?: string;
}

interface UpgradeModalProps {
  isMinSm: boolean;
  isSubmitting: boolean;
  onHideModal: () => void;
  onSubmit: () => void;
  showModal: boolean;
  trialOffer: TrialOffer | null;
}

interface UpgradeAccountFormProps {
  confirmation?:
    | false
    | {
        onHide: () => void;
        onShow: () => void;
        show: boolean;
      };
  description?: ReactNode;
  footer?: ReactNode;
  header?: ReactNode;
  isSubmitting: boolean;
  onSubmit: (data: PaymentFormSubmitData) => void | Promise<void>;
  paymentButtonLabel?: string;
  paymentDetailsLabel?: string | null;
  savedCardAction?: ReactNode;
  secondaryAction?: ReactNode;
  trial?: TrialConfig;
  user: User;
  variant?: 'card' | 'inline';
}

const UpgradeModal = ({ isMinSm, isSubmitting, onHideModal, onSubmit, showModal, trialOffer }: UpgradeModalProps) => (
  <Modal onHide={onHideModal} show={showModal} centered>
    <div className="p-3">
      <h4 className="fw-bold" data-testid="subscriptions-upgrade-modal-title">
        {trialOffer ? 'Confirm your free trial' : 'Confirm purchase'}
      </h4>
      <div>{trialOffer ? <TrialOfferSummary {...trialOffer} /> : <strong>Platinum subscription</strong>}</div>
      {!trialOffer && (
        <div className="my-4" data-testid="subscriptions-upgrade-modal-plan">
          You will receive a new invoice for this subscription
        </div>
      )}
      <div className={classnames({ 'd-inline-block': isMinSm, 'col-md-12 mb-2': !isMinSm })}>
        <Button
          disabled={isSubmitting}
          onClick={onSubmit}
          width={isMinSm ? 'content' : 'block'}
          text="bold"
          variant="primary-dark"
        >
          {trialOffer ? 'Confirm free trial' : 'Confirm purchase'}
        </Button>
      </div>
      <div className={classnames({ 'd-inline-block ms-2': isMinSm, 'col-md-12 mb-2': !isMinSm })}>
        <Button
          onClick={onHideModal}
          testId="subscriptions-upgrade-modal-action-button"
          text="bold"
          width={isMinSm ? 'content' : 'block'}
          variant="border"
        >
          Cancel
        </Button>
      </div>
    </div>
  </Modal>
);

const UpgradeAccountForm = ({
  confirmation = {
    onHide: noop,
    onShow: noop,
    show: false,
  },
  description = null,
  footer,
  header,
  isSubmitting,
  onSubmit,
  paymentButtonLabel,
  paymentDetailsLabel = 'Payment details',
  savedCardAction = null,
  secondaryAction,
  trial,
  user,
  variant = 'card',
}: UpgradeAccountFormProps) => {
  const { handleSubmit } = useForm();
  const { isMinSm, isMinXl } = useResponsiveWidth();
  const eligibleForFreeTrial = user.features.eligible_for_free_trial;
  const hasPaymentMethod = user.features.has_payment_method;
  const eligibleTrialPrices =
    trial === undefined && eligibleForFreeTrial ? billing.trialPrices(initialState.prices) : trial?.prices;
  const trialOffer = eligibleTrialPrices?.length
    ? ({ ...billing.trialOffer(eligibleTrialPrices), note: trial?.note } as TrialOffer)
    : null;
  const defaultHeader = trialOffer ? (
    <h3 className="fw-bold">Start your 30-day free trial</h3>
  ) : (
    <>
      <h3 className="fw-bold">Upgrade to Platinum</h3>
      <div className="mt-3">
        <div className="fw-bold text-1_125">$49 / Monthly</div>
      </div>
    </>
  );
  const defaultPaymentButtonLabel = trialOffer ? 'Start my 30-day free trial' : 'Upgrade to Platinum';
  const showConfirmation = confirmation !== false;
  const onShowConfirmation = confirmation ? confirmation.onShow : noop;
  const defaultSavedCardAction = (
    <div>
      <Button disabled={isSubmitting} onClick={onShowConfirmation} text="bold" variant="primary-dark">
        {defaultPaymentButtonLabel}
      </Button>
    </div>
  );
  const defaultFooter = (
    <span>
      You can downgrade your subscriptions at anytime
      {trialOffer && ' during your trial to avoid being automatically charged at the end'}.
    </span>
  );
  const footerContent = footer === undefined ? defaultFooter : footer;
  const isCard = variant === 'card';
  const className = isCard ? 'card' : '';
  const headerClassName = isCard ? 'card-header' : 'mb-3';
  const bodyClassName = isCard ? 'card-body' : 'd-flex flex-column';
  const style = isCard ? { minWidth: 400 } : undefined;

  return (
    <div
      className={classnames(className, {
        'flex-grow-1': isCard && !isMinXl,
        'flex-basis-50': isCard && isMinXl,
      })}
      style={style}
    >
      {showConfirmation && (
        <UpgradeModal
          isMinSm={isMinSm}
          isSubmitting={isSubmitting}
          onHideModal={confirmation.onHide}
          onSubmit={handleSubmit(onSubmit)}
          showModal={confirmation.show}
          trialOffer={trialOffer}
        />
      )}
      {(header !== null || trialOffer) && (
        <div className={headerClassName}>
          {header ?? defaultHeader}
          {trialOffer && <TrialOfferSummary className="mt-3" {...trialOffer} />}
        </div>
      )}
      <div className={bodyClassName}>
        {description && <div className="mb-5">{description}</div>}
        <div className="d-flex flex-column">
          {hasPaymentMethod ? (
            <>
              <CurrentCard user={user} />
              {savedCardAction ?? defaultSavedCardAction}
            </>
          ) : (
            <>
              {paymentDetailsLabel && <div className="fw-bold mb-5 text-1_125">{paymentDetailsLabel}</div>}
              <PaymentForm
                buttonLabel={paymentButtonLabel ?? defaultPaymentButtonLabel}
                isSubmitting={isSubmitting}
                onSubmit={onSubmit}
              />
              {secondaryAction}
            </>
          )}
        </div>
        {footerContent && <div className={classnames('text-0_75', { 'mt-3': hasPaymentMethod })}>{footerContent}</div>}
      </div>
    </div>
  );
};

export default UpgradeAccountForm;
